import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUiMode } from "@/contexts/UiModeContext";
import api from "@/lib/api";
import {
  LogOut, ChevronDown, Wallet, ArrowDownToLine, ShieldAlert,
  Calculator as CalcIcon, GitBranch, Sparkles, KeyRound, Users, Share2, Award,
  Menu, X, LayoutDashboard, Network, GraduationCap, Eye, EyeOff, Mic, Activity, Rocket, Mail, Fingerprint, Tag, UserCog, Building2, HelpCircle, Home, Cpu, Bot, Workflow, Zap, ShieldCheck, WifiOff,
} from "lucide-react";
import Logo from "@/components/Logo";
import WalletSwitcher from "@/components/WalletSwitcher";
import { Switch } from "@/components/ui/switch";
import CaPriceBadge from "@/components/CaPriceBadge";
import NotificationsBell from "@/components/NotificationsBell";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import ProfileModal from "@/components/ProfileModal";
import Avatar from "@/components/Avatar";
import { scrollToSection } from "@/lib/scrollToSection";
import FeatureRequestModal from "@/components/FeatureRequestModal";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const linkCls = ({ isActive }) => `px-2.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${isActive ? "bg-white/70 text-ink" : "text-ink-2 hover:bg-white/50"}`;

const TOOL_ITEMS = [
  { to: "/calculator", label: "Calculator", testid: "nav-calculator", icon: CalcIcon, desc: "Profit projections" },
  { to: "/liquid-value", label: "Liquid value", testid: "nav-liquid-value", icon: Wallet, desc: "If you sold everything today" },
  { to: "/flow", label: "How it works", testid: "nav-flow", icon: GitBranch, desc: "Visual flow diagram" },
  { to: "/compensation", label: "Compensation Plan", testid: "nav-compensation", icon: Award, desc: "VIP levels & rewards" },
];

// Always-visible admin quick jumps — sit under the wallet switcher and follow
// you across every page so the go-to screens are one tap away.
const ADMIN_SHORTCUTS = [
  { to: "/admin/share-links", label: "Share links", icon: Share2, testid: "share-links" },
  { to: "/admin/members", label: "Members", icon: Users, testid: "members" },
  { to: "/admin/trainees", label: "Trainees", icon: GraduationCap, testid: "trainees" },
  { to: "/calculator", label: "Calculator", icon: CalcIcon, testid: "calculator" },
];

function NavGroup({ label, items, testid, badge = 0 }) {
  const { pathname } = useLocation();
  const active = items.some((it) => pathname === it.to || pathname.startsWith(it.to + "/") || pathname.startsWith(it.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid={testid} className={`px-2.5 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap inline-flex items-center gap-1 outline-none transition-colors ${active ? "bg-white/70 text-ink" : "text-ink-2 hover:bg-white/50"}`}>
          {label} {badge > 0 && <UnreadBadge count={badge} testid={`${testid}-badge`} />} <ChevronDown size={13} className="opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={10} className="min-w-[230px] bg-white/95 backdrop-blur-xl border border-black/[0.06] p-1.5 shadow-2xl" style={{ borderRadius: 16 }}>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <DropdownMenuItem key={it.to} asChild className="rounded-lg cursor-pointer px-2.5 py-2 focus:bg-white/70">
              <Link to={it.to} data-testid={it.testid} className="flex items-center gap-2.5 w-full">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(15,18,38,0.05)" }}><Icon size={14} className="text-ink-2" /></span>
                <span className="flex-1 leading-tight">
                  <span className="block text-sm font-medium text-ink">{it.label}</span>
                  <span className="block text-[11px] text-ink-3">{it.desc}</span>
                </span>
                {it.badge > 0 && <UnreadBadge count={it.badge} testid={`${it.testid}-unread-badge`} />}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UnreadBadge({ count, testid }) {
  if (!count) return null;
  return (
    <span
      className="min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
      style={{ background: "#DC2626" }}
      data-testid={testid}
    >
      {count}
    </span>
  );
}

export default function Header({ variant = "public" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [cpOpen, setCpOpen] = useState(false);
  const [frOpen, setFrOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminUnread, setAdminUnread] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  const doLogout = async () => { await logout(); navigate("/login"); };
  // The demo mirrors an admin so the numbers are real, but it never gets the
  // admin nav — it's a read-only showcase of the member experience.
  const role = user?.is_demo ? "participant" : user?.role;
  const canMomentum = !!user?.momentum_access && !user?.is_demo;
  // A member who also runs a white-label firm switches between the two from here.
  const canPartnerConsole = !!user?.partner_access && !!user?.partner_id
    && user?.role !== "partner" && !user?.is_demo;
  const locked = !!user?.onboarding_locked; // gated new members see no nav
  // Old accounts were created without a phone — flag it until they add one.
  const needsPhone = !!user && !user.is_demo && !(user.phone || "").trim();
  const { lite, setLite, showCommunity, setShowCommunity } = useUiMode();
  const LITE_PATHS = ["/dashboard", "/calculator"];
  const switchMode = (toLite) => {
    setLite(toLite);
    if (toLite && user && !LITE_PATHS.some((p) => pathname.startsWith(p))) navigate("/dashboard");
  };

  // Red notify for the admin: unanswered member questions.
  useEffect(() => {
    if (role !== "admin" || user?.is_demo) return;
    let live = true;
    const load = async () => {
      try {
        const { data } = await api.get("/support/admin/unread-total");
        if (live) setAdminUnread(data.count || 0);
      } catch { /* polling — silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { live = false; clearInterval(t); };
  }, [role, user?.is_demo]);

  // Visitors shouldn't have to scroll the landing page to find the basics.
  const PUBLIC_SECTIONS = [
    { id: "top", label: "Home", icon: Home },
    { id: "what-is-botchain", label: "What is BOT Chain", icon: Network },
    { id: "what-is-carypact", label: "What is CaryPact", icon: Cpu },
    { id: "soren", label: "Ask Søren", icon: Bot },
    { id: "faq", label: "FAQ", icon: HelpCircle },
  ];
  const goSection = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (pathname !== "/") { navigate("/", { state: { scrollTo: id } }); return; }
    scrollToSection(id);
  };

  const mobileItems = user
    ? lite
      ? [
          { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
          { to: "/calculator", label: "Calculator", icon: CalcIcon },
        ]
      : [
        { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
        { to: "/wallets", label: "Wallets", icon: Wallet },
        ...(role === "admin" && !user?.is_demo ? [{ to: "/wallet-sweep", label: "Wallet sweep · soon", icon: Zap }] : []),
        ...TOOL_ITEMS,
        { to: "/org-chart", label: "My Community", icon: Network },
        ...(role === "admin" ? [
          { to: "/admin/members", label: "Members", icon: Users, badge: adminUnread },
          { to: "/admin/trainees", label: "Trainees", icon: GraduationCap },
          { to: "/admin/trace", label: "Trace user", icon: Fingerprint },
          { to: "/admin/share-links", label: "Share links", icon: Share2 },
          { to: "/admin/activity", label: "Activity", icon: Activity },
          { to: "/admin/not-reporting", label: "Not reporting", icon: WifiOff },
          { to: "/pre-deploy-check", label: "Pre-deploy check", icon: ShieldCheck },
            ...(user?.is_super_admin ? [{ to: "/admin/email-log", label: "Email log", icon: Mail },
              { to: "/admin/partners", label: "Partners", icon: Building2 },
              { to: "/admin/rpa", label: "Automations", icon: Workflow }] : []),
        ] : []),
        ...(canMomentum ? [{ to: "/operation-momentum", label: "Operation Momentum", icon: Rocket }] : []),
        ...(canPartnerConsole ? [{ to: "/partner", label: "Partner console", icon: Building2 }] : []),
        ...(role !== "admin" && user?.marketing_access ? [{ to: "/admin/share-links", label: "UTM links", icon: Tag }] : []),
        ...(role === "affiliate_manager" ? [{ to: "/my-team", label: "My Team", icon: Users }] : []),
        { to: "/ask", label: "Ask Søren", icon: Bot },
      ]
    : [
        ...PUBLIC_SECTIONS.map((s) => ({ section: s.id, label: s.label, icon: s.icon })),
      ];

  const mainNav = (
        <nav className="hidden lg:flex items-center gap-1 flex-wrap min-w-0">
        {!user && variant === "public" && (
          <>
            {PUBLIC_SECTIONS.map((s) => (
              <a key={s.id} href={s.id === "top" ? "/" : `/#${s.id}`} onClick={goSection(s.id)}
                className={linkCls({ isActive: false })} data-testid={`nav-section-${s.id}`}>
                {s.label}
              </a>
            ))}
          </>
        )}
        {user && !locked && lite && (
          <>
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-lite-dashboard">Dashboard</NavLink>
            <NavLink to="/calculator" className={linkCls} data-testid="nav-lite-calculator">Calculator</NavLink>
          </>
        )}
        {user && !locked && !lite && (
          <>
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink>
            <NavLink to="/wallets" className={linkCls} data-testid="nav-wallets">Wallets</NavLink>
            {role === "admin" && !user?.is_demo && (
              <NavLink to="/wallet-sweep" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`}
                data-testid="nav-wallet-sweep" title="Still under test — connect each of your TokenPocket wallets so its CaryPact figures can be read">
                <Zap size={13} style={{ color: "#C2185B" }} /> Wallet sweep
                <span className="text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                  style={{ background: "rgba(180,83,9,0.12)", color: "#B45309" }}>soon</span>
              </NavLink>
            )}
            <NavGroup label="Tools" items={TOOL_ITEMS} testid="nav-tools" />
            <NavLink to="/org-chart" className={linkCls} data-testid="nav-org-chart">My Community</NavLink>
            {role === "affiliate_manager" && (
              <NavLink to="/my-team" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`} data-testid="nav-my-team">
                <Users size={13} /> My Team
              </NavLink>
            )}
            {canMomentum && (
              <NavLink to="/operation-momentum" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`} data-testid="nav-operation-momentum" title="New member & video view reporting">
                <Rocket size={13} style={{ color: "#7C3AED" }} /> Operation Momentum
              </NavLink>
            )}
            {canPartnerConsole && (
              <NavLink to="/partner" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`} data-testid="nav-partner-console" title="Your white-label partner console — same account">
                <Building2 size={13} style={{ color: "#C2185B" }} /> Partner console
              </NavLink>
            )}
            {role === "admin" && (
              <NavGroup label="Users" testid="nav-users" badge={adminUnread}
                items={[
                  { to: "/admin/members", label: "Members", icon: Users, desc: "Accounts, approvals & messages", testid: "nav-members", badge: adminUnread },
                  { to: "/admin/trainees", label: "Trainees", icon: GraduationCap, desc: "Onboarding progress & interest leads", testid: "nav-trainees" },
                  { to: "/admin/trace", label: "Trace user", icon: Fingerprint, desc: "Where anyone came from + full journey", testid: "nav-trace" },
                  { to: "/admin/share-links", label: "Share links", icon: Share2, desc: "Your funnel, discover & invite links", testid: "nav-share-links" },
                  { to: "/admin/activity", label: "Activity", icon: Activity, desc: "View-as, resets & status changes", testid: "nav-activity" },
                  { to: "/admin/not-reporting", label: "Not reporting", icon: WifiOff, desc: "Wallets whose CaryPact numbers have stopped moving", testid: "nav-not-reporting" },
                  { to: "/pre-deploy-check", label: "Pre-deploy check", icon: ShieldCheck, desc: "What every number said last time, against now", testid: "nav-pre-deploy-check" },
                  ...(user?.is_super_admin ? [{ to: "/admin/email-log", label: "Email log", icon: Mail, desc: "Every email sent, per member", testid: "nav-email-log" },
                    { to: "/admin/partners", label: "Partners", icon: Building2, desc: "White-label firms · their logins & clients", testid: "nav-partners" },
                    { to: "/admin/rpa", label: "Automations", icon: Workflow, desc: "Recipes that move and restake on a schedule", testid: "nav-rpa" }] : []),
                ]} />
            )}
            {!user?.is_demo && (
              <NavLink to="/ask" className={({ isActive }) => `${linkCls({ isActive })} flex items-center gap-1.5`} data-testid="nav-ask">
                <Bot size={14} style={{ color: "#6B4FFF" }} /> Ask Søren
              </NavLink>
            )}
          </>
        )}
      </nav>
  );

  // The nav sits one layer above page content (which tops out at z-40) so the
  // wallet switcher's panel can't be painted over by a page's own toolbar — and
  // still below the modals and overlays at z-50.
  return (
    <header className="sticky top-0 z-[45] px-3 sm:px-6 md:px-10 pt-5">
      <div className="glass relative z-10 max-w-[1400px] mx-auto px-3 sm:px-5 md:px-7" style={{ borderRadius: 18 }}>
      {/* Row 1 — brand on the left, the account cluster on the right with room to
          breathe (the nav used to squeeze it into the panel's edge). */}
      <div className="h-14 flex items-center justify-between gap-3">
        <Link to={user && lite ? "/dashboard" : "/"} className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
          <Logo size="sm" />
        </Link>


        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          {user && !locked && (
            <div className="hidden sm:inline-flex items-center p-0.5 rounded-full" style={{ background: "rgba(15,18,38,0.06)" }} data-testid="portal-mode-toggle" title="Full portal or the lite affiliate view (dashboard · withdrawals · calculator)">
              {[["full", "Full"], ["lite", "Lite"]].map(([m, label]) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => switchMode(m === "lite")}
                  className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
                  style={(m === "lite") === lite ? { background: "white", color: "#0F1226", boxShadow: "0 1px 4px rgba(15,18,38,0.12)" } : { color: "#7A7F9A" }}
                  data-testid={`portal-mode-${m}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {!lite && <CaPriceBadge />}
          {/* On phones the switcher moves into the menu panel so the hamburger always fits. */}
          {!locked && <span className="hidden sm:block"><WalletSwitcher /></span>}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/50 transition-colors" data-testid="user-menu-trigger">
                  <span className="relative inline-flex">
                    <Avatar seed={user.avatar} name={user.is_demo ? "John" : user.name} email={user.is_demo ? "john" : user.email} size={28} testid="header-avatar" />
                    {needsPhone && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ background: "#F59E0B", boxShadow: "0 0 0 2px white" }} data-testid="header-phone-dot" />
                    )}
                  </span>
                  <span className="hidden md:inline text-left leading-tight">
                    <span className="block text-[12px] font-semibold text-ink truncate max-w-[110px]" data-testid="header-handle-name">
                      {user.is_demo ? "John" : (user.first_name || (user.name || user.email || "").split(" ")[0])}
                    </span>
                    <span className="block text-[10px] font-mono truncate max-w-[110px]" style={{ color: needsPhone ? "#B45309" : "#7A7F9A" }} data-testid="header-handle-phone">
                      {needsPhone ? "add your phone" : (user.phone || user.role)}
                    </span>
                  </span>
                  <ChevronDown size={15} className="text-ink-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="min-w-[220px] bg-white/95 backdrop-blur-xl border border-black/[0.06] p-1.5 shadow-2xl" style={{ borderRadius: 14 }}>
                <div className="px-2.5 py-2">
                  <div className="text-[11px] text-ink-3">Signed in as</div>
                  {/* The showcase account browses as a nameless member — never "demo". */}
                  <div className="text-sm font-medium truncate" data-testid="user-menu-email">{user.is_demo ? "John" : user.email}</div>
                  {!user.is_demo && (
                    <div className="text-[11px] font-mono mt-0.5" style={{ color: needsPhone ? "#B45309" : "#7A7F9A" }}>
                      {needsPhone ? "no phone on file" : user.phone}
                    </div>
                  )}
                </div>
                <div className="h-px bg-black/5 my-1" />
                {!user.is_demo && (
                  <DropdownMenuItem onClick={() => setProfileOpen(true)} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="menu-profile">
                    <UserCog size={15} className="text-ink-2" />
                    <span className="flex-1">Your handle</span>
                    {needsPhone && <span className="chip chip-amber !py-0.5 !px-1.5 !text-[10px]">phone</span>}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setCpOpen(true)} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" data-testid="menu-change-password"><KeyRound size={15} className="text-ink-2" /> Change password</DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  onClick={() => setShowCommunity(!showCommunity)}
                  className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70"
                  title="Hide community results across the portal — handy when recording videos about mining & staking"
                  data-testid="menu-community-toggle"
                >
                  {showCommunity ? <Eye size={15} className="text-ink-2" /> : <EyeOff size={15} className="text-ink-2" />}
                  <span className="flex-1">Community {showCommunity ? "shown" : "hidden"}</span>
                  <Switch checked={showCommunity} className="pointer-events-none" data-testid="menu-community-switch" />
                </DropdownMenuItem>
                {role === "admin" && !user?.is_demo && (
                  <DropdownMenuItem onClick={() => setFrOpen(true)} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70" title="Record a feature idea with your microphone — it's transcribed and sent to the BotChainCP developer" data-testid="menu-feature-request">
                    <Mic size={15} className="text-ink-2" /> Recommend a feature
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={doLogout} className="rounded-lg cursor-pointer px-2.5 py-2 flex items-center gap-2.5 focus:bg-white/70 text-rose-600" data-testid="menu-logout"><LogOut size={15} /> Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="btn-secondary" style={{ padding: "8px 16px", fontSize: 13 }} data-testid="login-link">Login</Link>
          )}
          <button
            type="button"
            className={locked ? "hidden" : "lg:hidden shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-ink-2 hover:bg-white/60 transition-colors"}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>

      {/* Row 2 — the menu itself, left-aligned in what used to be dead white
          space, with the admin shortcuts on the right of the same row. */}
      <div className="flex items-center gap-3 pb-2 -mt-1">
        {mainNav}
      {user && !locked && !user?.is_demo && (
        <div className="flex justify-end px-1 ml-auto" data-testid="admin-shortcut-bar">
          <div className="inline-flex items-center gap-1 px-1 py-0.5 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }}>
            {(role === "admin"
              ? ADMIN_SHORTCUTS
              : user?.marketing_access
                ? [{ to: "/admin/share-links", label: "UTM links", icon: Tag, testid: "utm-links" }]
                : []).map((s) => {
              const Ic = s.icon;
              return (
                <NavLink key={s.to + s.label} to={s.to} data-testid={`shortcut-${s.testid}`}
                  className={({ isActive }) => `px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-colors whitespace-nowrap ${isActive ? "bg-white text-ink shadow-sm" : "text-ink-2 hover:bg-white/60"}`}>
                  <Ic size={13} /> <span className="hidden sm:inline">{s.label}</span>
                </NavLink>
              );
            })}
            <NotificationsBell />
          </div>
        </div>
      )}
      </div>
      </div>

      {mobileOpen && !locked && (
        <div className="lg:hidden glass max-w-[1400px] mx-auto mt-2 p-3 max-h-[72vh] overflow-y-auto" style={{ borderRadius: 18 }} data-testid="mobile-menu">
          {user && (
            <div className="sm:hidden mb-2 pb-2 border-b border-black/5" data-testid="mobile-wallet-switcher">
              <WalletSwitcher />
            </div>
          )}
          <nav className="grid grid-cols-2 sm:grid-cols-3 gap-1">
            {mobileItems.map((it) => {
              const Icon = it.icon;
              if (it.section) {
                return (
                  <button key={it.section} onClick={goSection(it.section)}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 text-ink-2 hover:bg-white/50 text-left"
                    data-testid={`mobile-nav-section-${it.section}`}>
                    {Icon ? <Icon size={14} className="shrink-0" /> : null} {it.label}
                  </button>
                );
              }
              return (
                <NavLink
                  key={it.to + it.label}
                  to={it.to}
                  end={it.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `px-3 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 ${isActive ? "bg-white/70 text-ink" : "text-ink-2 hover:bg-white/50"}`}
                  data-testid={`mobile-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {Icon ? <Icon size={14} className="shrink-0" /> : null} {it.label}
                  <UnreadBadge count={it.badge} testid={`mobile-badge-${it.label.toLowerCase()}`} />
                </NavLink>
              );
            })}
          </nav>
        </div>
      )}

      <ChangePasswordModal open={cpOpen} onClose={() => setCpOpen(false)} />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
      {frOpen && <FeatureRequestModal onClose={() => setFrOpen(false)} />}
    </header>
  );
}
